import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Checkout = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [shippingAddress, setShippingAddress] = useState(user?.address || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [promoCode, setPromoCode] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || user === false) {
      navigate('/login');
      return;
    }
    fetchCart();
  }, [user]);

  const fetchCart = async () => {
    try {
      const { data } = await axios.get(`${API}/cart`, { withCredentials: true });
      if (data.length === 0) {
        toast.error('Votre panier est vide');
        navigate('/cart');
        return;
      }
      setCartItems(data);
    } catch (error) {
      console.error('Error fetching cart:', error);
      toast.error('Erreur lors du chargement du panier');
    }
  };

  const total = cartItems.reduce((sum, item) => sum + (item.product?.price || 0) * item.quantity, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!shippingAddress || !phone) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    try {
      // Create order
      const { data: orderData } = await axios.post(
        `${API}/orders`,
        { shipping_address: shippingAddress, phone, promo_code: promoCode },
        { withCredentials: true }
      );

      // Create checkout session
      const originUrl = window.location.origin;
      const { data: checkoutData } = await axios.post(
        `${API}/payments/checkout`,
        { order_id: orderData.order_id, origin_url: originUrl },
        { withCredentials: true }
      );

      // Redirect to Stripe
      window.location.href = checkoutData.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la commande');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold mb-8" style={{ fontFamily: 'IBM Plex Sans' }}>
          Paiement
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Shipping Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-sm border border-border p-6">
              <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'IBM Plex Sans' }}>
                Informations de livraison
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Adresse de livraison *
                  </label>
                  <textarea
                    value={shippingAddress}
                    onChange={(e) => setShippingAddress(e.target.value)}
                    required
                    rows="3"
                    className="w-full px-4 py-2 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="123 rue Example, 75001 Paris"
                    data-testid="shipping-address-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="06 12 34 56 78"
                    data-testid="phone-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Code promo (optionnel)
                  </label>
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    className="w-full px-4 py-2 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="CODE10"
                    data-testid="promo-code-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-6 btn-primary py-4 rounded-sm font-semibold disabled:opacity-50"
                data-testid="proceed-to-payment-button"
              >
                {loading ? 'Redirection...' : 'Procéder au paiement'}
              </button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-sm border border-border p-6 sticky top-24">
              <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'IBM Plex Sans' }}>
                Résumé
              </h2>
              <div className="space-y-3">
                {cartItems.map((item) => (
                  <div key={item.product_id} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {item.product?.name} x{item.quantity}
                    </span>
                    <span className="font-medium">
                      {((item.product?.price || 0) * item.quantity).toFixed(2)} €
                    </span>
                  </div>
                ))}
                <div className="border-t border-border pt-3">
                  <div className="flex justify-between">
                    <span className="font-bold">Total</span>
                    <span className="font-bold text-xl text-primary" data-testid="checkout-total">
                      {total.toFixed(2)} €
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;